// AICA AnnotationModal Component
// Modal for creating annotations on transcripts/analyses

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/contexts/ToastContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { logError } from '@/lib/logger';
import type { ApiClient } from '@/lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Save, X } from 'lucide-react';
import { CONTROL_SQUARE } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

export interface AnnotationModalProps {
  analysisId: string;
  recordId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedText?: string;
  startPosition?: number;
  endPosition?: number;
  mode?: 'create' | 'edit';
  annotation?: {
    id: string;
    type: 'objection_handling' | 'discovery_gap' | 'tone_issue' | 'other';
    severity: 'low' | 'medium' | 'high';
    note: string;
    startPosition?: number | null;
    endPosition?: number | null;
  };
  /**
   * Caller-supplied apiClient (e.g. the embedded-mode client the caller
   * already resolved via useRecordDetail()). When omitted, falls back to
   * useApiClient()'s Clerk-based client — see AnnotationModalWithHookClient
   * below, the ONLY place that hook is called, so it never runs where
   * there is no ClerkProvider. Embedded (Bitrix24) mode explicitly skips
   * ClerkProvider (main.tsx `isEmbeddedRoute`), and useApiClient() ->
   * useAuthFetch() -> useClerk() throws without one — calling it
   * unconditionally here would crash the embedded record-detail panel.
   * Same precedent as IncidentModal.
   */
  apiClient?: ApiClient;
}

export function AnnotationModal({
  apiClient: apiClientProp,
  ...props
}: AnnotationModalProps) {
  if (apiClientProp) {
    return <AnnotationModalView {...props} apiClient={apiClientProp} />;
  }
  return <AnnotationModalWithHookClient {...props} />;
}

// Isolates the useApiClient() call so it is only ever mounted when the
// caller did NOT supply an apiClient — never in embedded mode, where the
// caller always supplies one.
function AnnotationModalWithHookClient(props: Omit<AnnotationModalProps, 'apiClient'>) {
  const { apiClient } = useApiClient();
  return <AnnotationModalView {...props} apiClient={apiClient} />;
}

interface AnnotationModalViewProps extends Omit<AnnotationModalProps, 'apiClient'> {
  apiClient: ApiClient;
}

function AnnotationModalView({
  analysisId,
  isOpen,
  onClose,
  onSuccess,
  selectedText,
  startPosition,
  endPosition,
  mode = 'create',
  annotation,
  apiClient,
}: AnnotationModalViewProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [type, setType] = useState<
    'objection_handling' | 'discovery_gap' | 'tone_issue' | 'other'
  >('other');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [note, setNote] = useState(selectedText || '');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNote('');
      setType('other');
      setSeverity('medium');
    } else if (mode === 'edit' && annotation) {
      setType(annotation.type);
      setSeverity(annotation.severity);
      setNote(annotation.note);
    }
  }, [isOpen, mode, annotation]);

  const handleClose = useCallback(() => {
    setType('other');
    setSeverity('medium');
    setNote('');
    onClose();
  }, [onClose]);

  // Focus trap (WCAG 2.1.2) — keeps Tab focus within the dialog while open.
  const containerRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Escape closes the dialog.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]);

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error(t('annotations.noteRequired'), t('annotations.noteRequiredMessage'));
      return;
    }

    try {
      setCreating(true);
      if (mode === 'edit' && annotation) {
        await apiClient.analyses.updateAnnotation(analysisId, annotation.id, {
          type,
          severity,
          note: note.trim(),
          startPosition: startPosition ?? undefined,
          endPosition: endPosition ?? undefined,
        });
        toast.success(t('annotations.updated', 'Annotation updated'));
      } else {
        const response = await apiClient.analyses.createAnnotation(analysisId, {
          type,
          severity,
          note: note.trim(),
          startPosition: startPosition ?? undefined,
          endPosition: endPosition ?? undefined,
        });
        if (response.annotation) {
          toast.success(t('annotations.created'), t('annotations.createdMessage'));
        }
      }
      onSuccess?.();
      handleClose();
    } catch (err) {
      logError(
        mode === 'edit' ? 'annotations.update_failed' : 'annotations.create_failed',
        { component: 'AnnotationModal', analysisId },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t(
          mode === 'edit' ? 'annotations.updateError' : 'annotations.createError',
          'Operation failed'
        ),
        err instanceof Error ? err.message : t('common:common.errorUnknown')
      );
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="annotation-modal-title"
      data-testid="annotation-modal-backdrop"
      className="fixed inset-0 z-modal flex items-center justify-center bg-bg-void/50 p-4"
    >
      <Card data-testid="annotation-modal" className="w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div
            data-testid="annotation-modal-header"
            className="flex items-center justify-between mb-4"
          >
            <h2
              id="annotation-modal-title"
              data-testid="annotation-modal-title"
              className="text-base font-medium text-fg"
            >
              {mode === 'edit'
                ? t('annotations.editAnnotation', 'Edit Annotation')
                : t('annotations.addAnnotation')}
            </h2>
            <button
              data-testid="annotation-modal-close-button"
              onClick={handleClose}
              className={cn(
                CONTROL_SQUARE.md,
                'flex items-center justify-center rounded-button hover:bg-bg-elevated transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              aria-label={t('accessibility.actions.close')}
            >
              <X className="h-5 w-5 text-fg-muted" />
            </button>
          </div>

          {/* Selected Text Preview */}
          {selectedText && (
            <div
              data-testid="annotation-modal-selected-text"
              className="mb-4 p-3 rounded-card bg-bg-elevated border border-border"
            >
              <p className="text-xs font-medium text-fg-muted mb-1">
                {t('annotations.selectedText')}:
              </p>
              <p className="text-sm text-fg italic line-clamp-3">{selectedText}</p>
            </div>
          )}

          {/* Form */}
          <div data-testid="annotation-modal-form" className="space-y-4">
            {/* Type */}
            <div>
              <Label htmlFor="annotation-type">{t('common:common.type')}</Label>
              <Select
                data-testid="annotation-modal-type-select"
                value={type}
                onValueChange={(value) =>
                  setType(
                    value as
                      'objection_handling' | 'discovery_gap' | 'tone_issue' | 'other'
                  )
                }
                options={[
                  {
                    value: 'objection_handling',
                    label: t('records:records.agentReview.markers.poorObjectionHandling'),
                  },
                  {
                    value: 'discovery_gap',
                    label: t('records:records.agentReview.markers.missingDiscovery'),
                  },
                  {
                    value: 'tone_issue',
                    label: t('records:records.agentReview.markers.toneIssues'),
                  },
                  { value: 'other', label: t('common:common.other') },
                ]}
                className="mt-1"
              />
            </div>

            {/* Severity */}
            <div>
              <Label htmlFor="annotation-severity">{t('common:common.severity')}</Label>
              <Select
                data-testid="annotation-modal-severity-select"
                value={severity}
                onValueChange={(value) => setSeverity(value as 'low' | 'medium' | 'high')}
                options={[
                  { value: 'low', label: t('common:common.low') },
                  { value: 'medium', label: t('common:common.medium') },
                  { value: 'high', label: t('common:common.high') },
                ]}
                className="mt-1"
              />
            </div>

            {/* Note */}
            <div>
              <Label htmlFor="annotation-note">{t('annotations.note')}</Label>
              <Textarea
                data-testid="annotation-modal-note-textarea"
                id="annotation-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('annotations.annotationPlaceholder')}
                minRows={4}
                maxLength={1000}
                className="mt-1"
              />
              <p
                data-testid="annotation-modal-note-counter"
                className="mt-1 text-xs text-fg-muted"
              >
                {t('annotations.charactersCount', { count: note.length, max: 1000 })}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div data-testid="annotation-modal-actions" className="flex gap-3 mt-6">
            <Button
              data-testid="annotation-modal-cancel-button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={creating}
              className="flex-1"
            >
              {t('common:common.cancel')}
            </Button>
            <Button
              data-testid="annotation-modal-submit-button"
              onClick={handleSubmit}
              isLoading={creating}
              disabled={creating || !note.trim()}
              size="sm"
              className="flex-1"
            >
              {!creating && <Save className="h-4 w-4 mr-2" />}
              {mode === 'edit'
                ? t('annotations.saveAnnotation', 'Save')
                : t('annotations.createAnnotation')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
